import { Link, useLocation } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import type { Goal } from "./goalsConfig";

interface GoalMenuPanelProps {
  goal: Goal;
  onNavigate: () => void;
}

const GoalMenuPanel = ({ goal, onNavigate }: GoalMenuPanelProps) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Already on the "browse everything" destination — a CTA back to the page
  // you're standing on is dead weight, not a shortcut.
  const onPrimaryPage = pathname === goal.primaryHref;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.16 }}
      className="absolute left-0 top-9 w-[420px] max-w-[92vw] rounded-2xl border border-border bg-card shadow-2xl z-50 overflow-hidden"
    >
      <div className="p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          You want to…
        </p>
        <h3 className="text-base font-bold text-foreground leading-tight mb-1">
          {goal.question}
        </h3>
        <p className="text-xs text-muted-foreground mb-4">{goal.blurb}</p>

        {!onPrimaryPage && (
          <Link
            to={goal.primaryHref}
            onClick={onNavigate}
            className="flex items-center justify-between gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors mb-4"
          >
            {goal.primaryLabel}
            <ArrowRight size={16} />
          </Link>
        )}

        <div className="space-y-1">
          {goal.links.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={onNavigate}
                className="group flex items-start gap-3 rounded-lg p-2 -mx-2 hover:bg-muted transition-colors"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand group-hover:bg-brand group-hover:text-brand-foreground transition-colors">
                  <Icon size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-semibold text-foreground leading-tight">
                    {link.label}
                  </span>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {link.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {goal.categoryLinks && goal.categoryLinks.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Browse by category
            </p>
            <div className="flex flex-wrap gap-1.5">
              {goal.categoryLinks.map((cat) => {
                const CatIcon = cat.icon;
                return (
                  <Link
                    key={cat.href}
                    to={cat.href}
                    onClick={onNavigate}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted transition-colors"
                  >
                    <CatIcon size={12} />
                    {cat.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {!user && (
        <Link
          to="/request-access"
          onClick={onNavigate}
          className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-brand/5 via-accent/5 to-brand/5 border-t border-border hover:from-brand/10 hover:to-brand/10 transition-colors group"
        >
          <p className="text-xs text-foreground">
            <span className="font-semibold">Members earn cash rewards</span>
            <span className="text-muted-foreground"> as they use these tools</span>
          </p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand whitespace-nowrap group-hover:gap-2 transition-all">
            Free account <ArrowRight size={12} />
          </span>
        </Link>
      )}
    </motion.div>
  );
};

export default GoalMenuPanel;
